import { Injectable } from '@angular/core';
import { AuthUser, Staff, WebserviceResponse } from "../interfaces";
import { catchError, map, retry } from "rxjs/operators";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { AlertService, IAlertType } from "./alert.service";
import { authBaseUrl, restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class StaffStoreService {
  private readonly endpoint = restBaseUrl();
  private readonly authEndpoint = authBaseUrl();

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService) { }

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  private mapStaffResponse(res: any): WebserviceResponse<Staff[]> {
    if (Array.isArray(res?.object)) {
      return res as WebserviceResponse<Staff[]>;
    }

    const data = Array.isArray(res?.data) ? res.data : res?.data ? [res.data] : [];
    const status = (res?.status ?? '').toString().toLowerCase();
    return {
      object: data,
      token: undefined,
      hasErrors: status !== 'success',
      message: res?.message ?? '',
      praefix: undefined,
      errors: [],
      firstError: undefined
    };
  }

  public getStaffById(staffID: number): Observable<WebserviceResponse<Staff[]>> {
    return this.http.get<any>(
        this.endpoint + '/staff/get_single_staff/' + staffID).pipe(
            map(res => this.mapStaffResponse(res)),
            retry(3),
            catchError(this.handleError.bind(this))
    );
  }

  public getStaff(): Observable<WebserviceResponse<Staff[]>> {
    return this.http.get<any>(
      this.endpoint + '/staff/get_staff').pipe(
      map(res => this.mapStaffResponse(res)),
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public createStaff(payload: Partial<Staff>): Observable<WebserviceResponse<Staff[]>> {
    return this.http.post<any>(
      this.endpoint + '/staff/add_staff',
      payload
    ).pipe(
      map(res => this.mapStaffResponse(res)),
      catchError(this.handleError.bind(this))
    );
  }

  public updateStaff(staffId: number, payload: Partial<Staff>): Observable<WebserviceResponse<Staff[]>> {
    const options = new HttpParams({ fromObject: { staff_id: staffId.toString() } });
    return this.http.put<any>(
      this.endpoint + '/staff/update_staff',
      payload,
      { params: options }
    ).pipe(
      map(res => this.mapStaffResponse(res)),
      catchError(this.handleError.bind(this))
    );
  }

  public deleteStaff(staffId: number): Observable<WebserviceResponse<Staff[]>> {
    const options = new HttpParams({ fromObject: { staff_id: staffId.toString() } });
    return this.http.delete<any>(
      this.endpoint + '/staff/delete_staff',
      { params: options }
    ).pipe(
      map(res => this.mapStaffResponse(res)),
      catchError(this.handleError.bind(this))
    );
  }

  public getAuthUsers(): Observable<AuthUser[]> {
    return this.http.get<AuthUser[]>(
      this.authEndpoint + '/users'
    ).pipe(
      retry(2),
      catchError(this.handleError.bind(this))
    );
  }

  public createAuthUser(payload: {
    email: string;
    password: string;
    display_name?: string;
    crm_username?: string | null;
    is_active: boolean;
    is_admin: boolean;
    staff_id: number;
  }): Observable<AuthUser> {
    return this.http.post<AuthUser>(
      this.authEndpoint + '/users',
      payload
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }

  public updateAuthUser(userId: string, payload: {
    email?: string;
    display_name?: string;
    crm_username?: string | null;
    is_active?: boolean;
    is_admin?: boolean;
    staff_id?: number;
    /** Nur senden, wenn ein neues Passwort gesetzt werden soll. */
    password?: string;
  }): Observable<AuthUser> {
    return this.http.patch<AuthUser>(
      this.authEndpoint + '/users/' + userId,
      payload
    ).pipe(
      catchError(this.handleError.bind(this))
    );
  }
}
